// paths.ts — maps MSW-era unprefixed paths (e.g. "/products") to the real DRF
// routes (e.g. "/api/master-data/catalog/") while we slice modules across.
//
// Hooks call `resolvePath("/products")` and get back:
//   - the original mock path when VITE_USE_REAL_API is off
//   - the real DRF path when it's on AND the path is mapped
//   - `null` when on but not yet wired → the hook should short-circuit to []

export const useRealApi = import.meta.env.VITE_USE_REAL_API === 'true';

// Mock path → real DRF path. Add entries as endpoints get wired up.
const MAP: Record<string, string> = {
  // --- Master Data ---
  '/products': '/api/master-data/products/',
  '/catalog': '/api/master-data/catalog/',
  '/bins': '/api/master-data/bins/',
  '/stations': '/api/master-data/stations/',
  '/policies': '/api/master-data/policies/',
  '/bulk-jobs': '/api/master-data/bulk-jobs/',
  '/bulk-exports': '/api/master-data/bulk-exports/',

  // --- Identity ---
  '/stores': '/api/identity/stores/',
  '/roles': '/api/identity/roles/',
  '/rbac-users': '/api/identity/users/',
  '/role-capabilities': '/api/identity/role-capabilities/',

  // --- Devices (handheld registry, DR-006) ---
  '/devices': '/api/devices/',

  // --- Inventory ---
  '/batches': '/api/inventory/batches/',
  '/store-products': '/api/inventory/store-products/',
  '/allocations': '/api/inventory/allocations/',
  '/movements': '/api/stocks/ledger/',
  '/audit': '/api/audit/trail/',
  '/adjustments': '/api/adjustments/',
  '/alerts': '/api/inventory/alerts/',

  // --- Stocks ---
  '/stk/inward': '/api/stocks/inward/',
  '/stk/inward-lines': '/api/stocks/inward-lines/',
  '/stk/dispense': '/api/stocks/outward/',
  '/stk/trays': '/api/stocks/trays/',
  '/stk/tray-master': '/api/stocks/tray-master/',
  '/stk/tray-master/available': '/api/stocks/tray-master/available/',
  '/stk/backorders': '/api/stocks/backorders/',
  '/stk/partpacks': '/api/stocks/part-packs/',
  '/stk/adjustments': '/api/adjustments/',
  '/stk/transfers': '/api/transfers/',
  '/stk/alerts': '/api/inventory/alerts/',
  '/stk/returns': '/api/stocks/returns/',
  '/stk/recalls': '/api/inventory/recalls/',
  '/reconciliation/sessions': '/api/inventory/stocktakes/',

  // --- Prescriptions ---
  '/rx/prescriptions': '/api/prescriptions/',

  // --- Multi-Store ---
  '/mst/requests': '/api/transfers/',

  // --- Global (⌘K) search ---
  '/search': '/api/search/',
};

// Detail/action paths: given a mock path like "/products/123", resolve the real route.
export function resolveDetailPath(mockPath: string): string | null {
  if (!useRealApi) return mockPath;
  // Match /entity/id or /entity/id/action
  const match = mockPath.match(/^(\/[^/]+)(\/.*)?$/);
  if (!match) return null;
  const base = MAP[match[1]];
  if (!base) return null;
  // e.g. /products/abc-123 → /api/master-data/catalog/abc-123/
  const suffix = match[2] ?? '';
  return `${base}${suffix.replace(/^\//, '')}${suffix && !suffix.endsWith('/') ? '/' : ''}`;
}

export interface ResolvedPath {
  path: string;
  paginated: boolean;
}

export function resolvePath(mockPath: string): ResolvedPath | null {
  if (!useRealApi) return { path: mockPath, paginated: false };
  const real = MAP[mockPath];
  if (!real) return null;
  return { path: real, paginated: true };
}
